import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { InlineMath, BlockMath } from "../Math";
import "katex/dist/katex.min.css";
import ParabolaChart, { PALETTE } from "./ParabolaChart";

/* ============ shared helpers ============ */

const Reveal = ({ showAt, fragmentIndex, children, className = "" }) => (
    <AnimatePresence initial={false}>
        {fragmentIndex >= showAt && (
            <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className={className}
            >
                {children}
            </motion.div>
        )}
    </AnimatePresence>
);

/**
 * Shell — transparent. Reuses the LessonStage ambient background &
 * orb. No competing gradient / fill of its own.
 */
const Shell = ({ children }) => (
    <div className="absolute inset-0 overflow-hidden">
        <div className="relative z-10 w-full h-full px-12 py-10 flex flex-col">
            {children}
        </div>
    </div>
);

const SlideHeader = ({ kicker, title }) => (
    <div className="mb-8 text-left">
        <p className="text-base uppercase tracking-[0.32em] text-[#4a5662] font-semibold mb-3">
            {kicker}
        </p>
        <h2 className="text-5xl font-semibold text-[#14202a] tracking-tight leading-tight">
            {title}
        </h2>
    </div>
);

/* ============ 1. what-is-quadratic ============ */
const WhatIsQuadraticSlide = ({ slide, fragmentIndex }) => (
    <Shell>
        <SlideHeader kicker="Concept · Definition" title={slide.title} />
        <div className="flex-1 grid grid-cols-12 gap-10 items-start max-w-6xl mx-auto w-full">
            <div className="col-span-7 space-y-5">
                <Reveal showAt={0} fragmentIndex={fragmentIndex}>
                    <div
                        className="p-9 text-center"
                        style={{
                            background: "rgba(255,255,255,0.85)",
                            borderRadius: 28,
                            boxShadow: "0 24px 60px rgba(15,23,42,0.10)",
                            border: "1px solid rgba(255,255,255,0.7)",
                            backdropFilter: "blur(18px)",
                        }}
                    >
                        <p className="text-sm uppercase tracking-[0.28em] text-[#4a5662] font-semibold mb-5">
                            Standard form
                        </p>
                        <div className="text-5xl">
                            <BlockMath
                                math={String.raw`y = \textcolor{#3b82f6}{a}x^2 + \textcolor{#10b981}{b}x + \textcolor{#f59e0b}{c}`}
                            />
                        </div>
                        <p className="text-lg text-[#4a5662] mt-4 font-medium">
                            with <InlineMath math="a \neq 0" />
                        </p>
                    </div>
                </Reveal>
                <Reveal showAt={1} fragmentIndex={fragmentIndex}>
                    <p className="text-2xl text-[#14202a] leading-relaxed">
                        The <strong>highest power of x is 2</strong> — that
                        single rule is what makes an equation quadratic, and
                        why its graph is always a parabola.
                    </p>
                </Reveal>
                <Reveal showAt={2} fragmentIndex={fragmentIndex}>
                    <div className="bg-amber-50/80 border-l-4 border-amber-500 p-5 rounded-r-2xl">
                        <p className="text-lg text-amber-900">
                            <strong>Why a ≠ 0?</strong> If <InlineMath math="a = 0" /> the <InlineMath math="x^2" /> term vanishes — the equation collapses to <InlineMath math="bx + c = 0" />, a straight line.
                        </p>
                    </div>
                </Reveal>
                <Reveal showAt={3} fragmentIndex={fragmentIndex}>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-white/70 border border-emerald-200 rounded-2xl text-center">
                            <p className="text-xs uppercase tracking-wider text-emerald-700 mb-2 font-semibold">
                                Quadratic ✓
                            </p>
                            <div className="text-lg space-y-1 text-[#14202a]">
                                <BlockMath math="2x^2 + 5x + 3 = 0" />
                                <BlockMath math="x^2 - 9 = 0" />
                            </div>
                        </div>
                        <div className="p-4 bg-white/70 border border-rose-200 rounded-2xl text-center">
                            <p className="text-xs uppercase tracking-wider text-rose-700 mb-2 font-semibold">
                                Not quadratic ✗
                            </p>
                            <div className="text-lg space-y-1 text-[#4a5662]">
                                <BlockMath math="3x + 5 = 0" />
                                <BlockMath math="x^3 + x + 1 = 0" />
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
            <div className="col-span-5 space-y-4">
                {[
                    {
                        rowCls: "border-blue-500",
                        textCls: "text-blue-500",
                        sym: "a",
                        title: "Quadratic Term",
                        body: "Controls direction & width of the parabola",
                    },
                    {
                        rowCls: "border-emerald-500",
                        textCls: "text-emerald-500",
                        sym: "b",
                        title: "Linear Term",
                        body: "Influences the axis of symmetry",
                    },
                    {
                        rowCls: "border-amber-500",
                        textCls: "text-amber-500",
                        sym: "c",
                        title: "Constant",
                        body: "The y-intercept anchor",
                    },
                ].map((row, i) => (
                    <Reveal key={row.sym} showAt={i} fragmentIndex={fragmentIndex}>
                        <div
                            className={`p-5 bg-white/80 rounded-2xl border-l-4 ${row.rowCls}`}
                            style={{ boxShadow: "0 12px 30px rgba(15,23,42,0.06)" }}
                        >
                            <div className="flex items-center gap-4">
                                <span
                                    className={`text-4xl font-bold ${row.textCls}`}
                                >
                                    <InlineMath math={row.sym} />
                                </span>
                                <div>
                                    <p className="text-xs uppercase tracking-wider text-[#4a5662] font-semibold">
                                        {row.title}
                                    </p>
                                    <p className="text-base text-[#14202a] mt-1 leading-relaxed">
                                        {row.body}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </Shell>
);

/* ============ 2. shape-of-x2 ============ */
const ShapeOfX2Slide = ({ slide, fragmentIndex }) => {
    const samplePoints = [
        { x: -3, y: 9 },
        { x: -2, y: 4 },
        { x: 2, y: 4 },
        { x: 3, y: 9 },
    ];
    return (
        <Shell>
            <SlideHeader kicker="Concept · The Parabola" title={slide.title} />
            <div className="flex-1 grid grid-cols-12 gap-10 items-start max-w-6xl mx-auto w-full">
                <div className="col-span-5 space-y-5">
                    <Reveal showAt={0} fragmentIndex={fragmentIndex}>
                        <div className="bg-white/80 p-5 rounded-2xl border border-white/70 shadow-sm">
                            <p className="text-xs uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2">
                                Step 1 — Pick values
                            </p>
                            <p className="text-xl text-[#14202a] leading-relaxed">
                                When <InlineMath math="x = \pm 2" />, <InlineMath math="y = 4" />. When <InlineMath math="x = \pm 3" />, <InlineMath math="y = 9" />.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal showAt={1} fragmentIndex={fragmentIndex}>
                        <div className="bg-white/80 p-5 rounded-2xl border-l-4 border-blue-500">
                            <p className="text-xs uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2">
                                Step 2 — Join smoothly
                            </p>
                            <p className="text-xl text-[#14202a] leading-relaxed">
                                Connecting the points gives a smooth U-shaped curve, symmetric about the y-axis.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal showAt={2} fragmentIndex={fragmentIndex}>
                        <div className="bg-blue-50/80 border-l-4 border-blue-500 p-5 rounded-r-2xl">
                            <p className="text-xl font-semibold text-blue-900 mb-2">
                                That U-shape is a parabola.
                            </p>
                            <p className="text-base text-blue-800">
                                The lowest point is the <strong>vertex</strong>. For <InlineMath math="y = x^2" />, the vertex sits at the origin <InlineMath math="(0, 0)" />.
                            </p>
                        </div>
                    </Reveal>
                </div>
                <div className="col-span-7">
                    <ParabolaChart
                        height={440}
                        range={[-5, 5]}
                        yRange={[-2, 12]}
                        stepX={1}
                        stepY={2}
                        curves={[
                            // Frag 0: just sample dots, no curve. Frag 1+: full curve.
                            {
                                id: "x2",
                                fn: (x) => x * x,
                                color: PALETTE.primary,
                                hidden: fragmentIndex < 1,
                                label: "y = x²",
                            },
                        ]}
                        markers={[
                            ...samplePoints.map((p) => ({
                                ...p,
                                color: PALETTE.indigo,
                            })),
                            {
                                x: 0,
                                y: 0,
                                color: PALETTE.amber,
                                label: "Vertex",
                                hidden: fragmentIndex < 2,
                            },
                        ]}
                    />
                </div>
            </div>
        </Shell>
    );
};

/* ============ 3. why-curves-open ============ */
const WhyCurvesOpenSlide = ({ slide, fragmentIndex }) => (
    <Shell>
        <SlideHeader kicker="Concept · The coefficient a" title={slide.title} />
        <div className="flex-1 grid grid-cols-12 gap-10 items-start max-w-6xl mx-auto w-full">
            <div className="col-span-5 space-y-4">
                <Reveal showAt={0} fragmentIndex={fragmentIndex}>
                    <p className="text-2xl text-[#14202a] leading-relaxed">
                        The <strong>coefficient of <InlineMath math="x^2" /></strong> — the value <span className="text-blue-500 font-bold">a</span> — controls whether the parabola opens up or down.
                    </p>
                </Reveal>
                {[
                    {
                        showAt: 1,
                        boxCls: "bg-emerald-50 border-emerald-200",
                        labelCls: "text-emerald-700",
                        textCls: "text-emerald-800",
                        label: "a > 0",
                        text: "Opens upward (U-shape)",
                    },
                    {
                        showAt: 2,
                        boxCls: "bg-rose-50 border-rose-200",
                        labelCls: "text-rose-700",
                        textCls: "text-rose-800",
                        label: "a < 0",
                        text: "Opens downward (∩-shape)",
                    },
                    {
                        showAt: 3,
                        boxCls: "bg-indigo-50 border-indigo-200",
                        labelCls: "text-indigo-700",
                        textCls: "text-indigo-800",
                        label: "|a| larger",
                        text: "Narrower (steeper)",
                    },
                    {
                        showAt: 3,
                        boxCls: "bg-teal-50 border-teal-200",
                        labelCls: "text-teal-700",
                        textCls: "text-teal-800",
                        label: "|a| smaller",
                        text: "Wider (flatter)",
                    },
                ].map((r) => (
                    <Reveal
                        key={r.label}
                        showAt={r.showAt}
                        fragmentIndex={fragmentIndex}
                    >
                        <div
                            className={`p-4 ${r.boxCls} border rounded-xl flex items-center justify-between`}
                        >
                            <span
                                className={`text-xl font-bold ${r.labelCls}`}
                            >
                                <InlineMath math={r.label.replace("|", "\\lvert ").replace("|", "\\rvert ")} />
                            </span>
                            <span
                                className={`text-base ${r.textCls} font-medium`}
                            >
                                {r.text}
                            </span>
                        </div>
                    </Reveal>
                ))}
            </div>
            <div className="col-span-7">
                <ParabolaChart
                    height={460}
                    range={[-4, 4]}
                    yRange={[-8, 8]}
                    stepY={2}
                    curves={[
                        {
                            id: "up",
                            fn: (x) => x * x,
                            color: PALETTE.green,
                            hidden: fragmentIndex < 1,
                            label: "y = x²",
                        },
                        {
                            id: "down",
                            fn: (x) => -x * x,
                            color: PALETTE.red,
                            hidden: fragmentIndex < 2,
                            label: "y = -x²",
                        },
                        {
                            id: "narrow",
                            fn: (x) => 3 * x * x,
                            color: PALETTE.indigo,
                            hidden: fragmentIndex < 3,
                            dashed: true,
                            label: "y = 3x²",
                        },
                        {
                            id: "wide",
                            fn: (x) => 0.4 * x * x,
                            color: PALETTE.teal,
                            hidden: fragmentIndex < 3,
                            dashed: true,
                            label: "y = 0.4x²",
                        },
                    ]}
                />
            </div>
        </div>
    </Shell>
);

/* ============ 4. roots-intercepts ============ */
const RootsInterceptsSlide = ({ slide, fragmentIndex }) => (
    <Shell>
        <SlideHeader kicker="Concept · Roots" title={slide.title} />
        <div className="flex-1 grid grid-cols-12 gap-10 items-start max-w-6xl mx-auto w-full">
            <div className="col-span-5 space-y-4">
                <Reveal showAt={0} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/80 p-5 rounded-2xl border border-white/70">
                        <p className="text-xl text-[#14202a] leading-relaxed">
                            <strong>Solving</strong> a quadratic means finding x-values where the graph meets the x-axis — i.e. where <InlineMath math="y = 0" />.
                        </p>
                    </div>
                </Reveal>
                <Reveal showAt={1} fragmentIndex={fragmentIndex}>
                    <div className="bg-indigo-50/80 border-l-4 border-indigo-500 p-5 rounded-r-2xl">
                        <p className="text-xl text-indigo-900">
                            These x-values are called <strong>roots</strong> (also <em>zeroes</em> or x-intercepts).
                        </p>
                    </div>
                </Reveal>
                <Reveal showAt={2} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/80 p-5 rounded-2xl border border-white/70">
                        <p className="text-xs uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-3">
                            Worked example
                        </p>
                        <div className="text-2xl text-center mb-3">
                            <BlockMath math="y = x^2 - 5x + 6" />
                        </div>
                        <div className="flex gap-3 justify-center">
                            <span className="px-5 py-2 rounded-xl bg-red-50 border-2 border-red-300 text-red-700 text-xl font-bold">
                                <InlineMath math="x = 2" />
                            </span>
                            <span className="px-5 py-2 rounded-xl bg-red-50 border-2 border-red-300 text-red-700 text-xl font-bold">
                                <InlineMath math="x = 3" />
                            </span>
                        </div>
                    </div>
                </Reveal>
            </div>
            <div className="col-span-7">
                <ParabolaChart
                    height={460}
                    range={[-1, 6]}
                    yRange={[-3, 10]}
                    stepY={2}
                    curves={[
                        {
                            id: "main",
                            fn: (x) => x * x - 5 * x + 6,
                            color: PALETTE.primary,
                            hidden: fragmentIndex < 0,
                            label: "y = x² - 5x + 6",
                        },
                    ]}
                    markers={[
                        { x: 2, y: 0, color: PALETTE.red, label: "x = 2", hidden: fragmentIndex < 2 },
                        { x: 3, y: 0, color: PALETTE.red, label: "x = 3", hidden: fragmentIndex < 2 },
                    ]}
                />
            </div>
        </div>
    </Shell>
);

/* ============ 5. factor-form-bridge ============ */
const FactorFormBridgeSlide = ({ slide, fragmentIndex }) => (
    <Shell>
        <SlideHeader kicker="Concept · Factor form" title={slide.title} />
        <div className="flex-1 grid grid-cols-12 gap-10 items-start max-w-6xl mx-auto w-full">
            <div className="col-span-7 space-y-5">
                <Reveal showAt={0} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/80 p-5 rounded-2xl border border-white/70">
                        <p className="text-lg text-[#14202a] mb-3">
                            If roots are <strong>2</strong> and <strong>3</strong>, each one gives a factor:
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-center text-xl">
                                <InlineMath math="x = 2 \;\Rightarrow\; (x - 2)" />
                            </div>
                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-center text-xl">
                                <InlineMath math="x = 3 \;\Rightarrow\; (x - 3)" />
                            </div>
                        </div>
                    </div>
                </Reveal>
                <Reveal showAt={1} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/85 p-6 rounded-2xl shadow-sm border border-white/70 text-center">
                        <p className="text-xs uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-3">
                            Factor form
                        </p>
                        <div className="text-3xl">
                            <BlockMath math="(x - 2)(x - 3) = 0" />
                        </div>
                    </div>
                </Reveal>
                <Reveal showAt={2} fragmentIndex={fragmentIndex}>
                    <div className="bg-indigo-50/80 border-l-4 border-indigo-500 p-5 rounded-r-2xl">
                        <p className="text-xs uppercase tracking-[0.22em] text-indigo-700 font-semibold mb-2">
                            Expand it back
                        </p>
                        <div className="text-xl text-indigo-900">
                            <BlockMath math="(x - 2)(x - 3) = x^2 - 5x + 6" />
                        </div>
                        <p className="text-base text-indigo-800 mt-2">
                            We end up exactly where we started — the two-way bridge between roots and standard form.
                        </p>
                    </div>
                </Reveal>
            </div>
            <div className="col-span-5">
                <ParabolaChart
                    height={400}
                    range={[-1, 6]}
                    yRange={[-3, 10]}
                    stepY={2}
                    curves={[
                        {
                            id: "main",
                            fn: (x) => x * x - 5 * x + 6,
                            color: PALETTE.primary,
                            label: "y = x² - 5x + 6",
                        },
                    ]}
                    markers={[
                        { x: 2, y: 0, color: PALETTE.red, label: "x = 2" },
                        { x: 3, y: 0, color: PALETTE.red, label: "x = 3" },
                    ]}
                />
            </div>
        </div>
    </Shell>
);

/* ============ 6. factorisation-method ============ */
const FactorisationMethodSlide = ({ slide, fragmentIndex }) => (
    <Shell>
        <SlideHeader kicker="Method · Factorisation" title={slide.title} />
        <div className="flex-1 grid grid-cols-12 gap-10 items-start max-w-6xl mx-auto w-full">
            <div className="col-span-7 space-y-4">
                <Reveal showAt={0} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/85 p-5 rounded-2xl border border-white/70 text-center">
                        <p className="text-xs uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-3">
                            The problem
                        </p>
                        <div className="text-3xl">
                            <BlockMath math="x^2 + 5x + 6 = 0" />
                        </div>
                    </div>
                </Reveal>
                <Reveal showAt={1} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/80 p-4 rounded-2xl border-l-4 border-blue-500">
                        <p className="text-xs uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2">
                            Step 1 — find the pair
                        </p>
                        <p className="text-lg text-[#14202a]">
                            Two numbers with <strong>product = 6</strong> and <strong>sum = 5</strong> → that's <span className="text-blue-700 font-mono font-bold">2</span> and <span className="text-blue-700 font-mono font-bold">3</span>.
                        </p>
                    </div>
                </Reveal>
                <Reveal showAt={2} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/80 p-4 rounded-2xl border-l-4 border-indigo-500">
                        <p className="text-xs uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2">
                            Step 2 — split middle term
                        </p>
                        <div className="text-xl text-center">
                            <BlockMath math="x^2 + 2x + 3x + 6 = 0" />
                        </div>
                    </div>
                </Reveal>
                <Reveal showAt={3} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/80 p-4 rounded-2xl border-l-4 border-emerald-500">
                        <p className="text-xs uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2">
                            Step 3 — group &amp; factor
                        </p>
                        <div className="text-lg text-center">
                            <BlockMath math="x(x + 2) + 3(x + 2) = 0 \;\Rightarrow\; (x + 2)(x + 3) = 0" />
                        </div>
                    </div>
                </Reveal>
                <Reveal showAt={4} fragmentIndex={fragmentIndex}>
                    <div className="bg-emerald-50/80 border border-emerald-200 p-4 rounded-2xl">
                        <p className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-semibold mb-3 text-center">
                            Roots
                        </p>
                        <div className="flex gap-4 justify-center">
                            <span className="px-5 py-2 rounded-xl bg-white border-2 border-emerald-300 text-emerald-700 text-xl font-bold">
                                <InlineMath math="x = -2" />
                            </span>
                            <span className="px-5 py-2 rounded-xl bg-white border-2 border-emerald-300 text-emerald-700 text-xl font-bold">
                                <InlineMath math="x = -3" />
                            </span>
                        </div>
                    </div>
                </Reveal>
            </div>
            <div className="col-span-5">
                <ParabolaChart
                    height={420}
                    range={[-5, 2]}
                    yRange={[-3, 10]}
                    stepY={2}
                    curves={[
                        {
                            id: "main",
                            fn: (x) => x * x + 5 * x + 6,
                            color: PALETTE.primary,
                            hidden: fragmentIndex < 1,
                            label: "y = x² + 5x + 6",
                        },
                    ]}
                    markers={[
                        { x: -2, y: 0, color: PALETTE.red, label: "x = -2", hidden: fragmentIndex < 4 },
                        { x: -3, y: 0, color: PALETTE.red, label: "x = -3", hidden: fragmentIndex < 4 },
                    ]}
                />
            </div>
        </div>
    </Shell>
);

/* ============ 7. completing-square ============ */
const CompletingSquareSlide = ({ slide, fragmentIndex }) => (
    <Shell>
        <SlideHeader kicker="Method · Completing the square" title={slide.title} />
        <div className="flex-1 grid grid-cols-12 gap-10 items-start max-w-6xl mx-auto w-full">
            <div className="col-span-7 space-y-4">
                <Reveal showAt={0} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/85 p-5 rounded-2xl border border-white/70">
                        <p className="text-xs uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-3">
                            The trick
                        </p>
                        <p className="text-lg text-[#14202a]">
                            Rewrite <InlineMath math="x^2 + 6x" /> as a perfect square. Half of 6 is <strong>3</strong>, and <InlineMath math="3^2 = 9" />.
                        </p>
                    </div>
                </Reveal>
                <Reveal showAt={1} fragmentIndex={fragmentIndex}>
                    <div className="bg-indigo-50/80 border-l-4 border-indigo-500 p-5 rounded-r-2xl text-center">
                        <div className="text-2xl">
                            <BlockMath math="x^2 + 6x + 9 = (x + 3)^2" />
                        </div>
                    </div>
                </Reveal>
                <Reveal showAt={2} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/80 p-4 rounded-2xl border-l-4 border-blue-500">
                        <p className="text-xs uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2">
                            Solve <InlineMath math="x^2 + 6x + 5 = 0" />
                        </p>
                        <div className="text-lg text-center">
                            <BlockMath math="\text{Step 1:}\; x^2 + 6x = -5" />
                        </div>
                    </div>
                </Reveal>
                <Reveal showAt={3} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/80 p-4 rounded-2xl border-l-4 border-blue-500">
                        <div className="text-lg text-center">
                            <BlockMath math="\text{Step 2:}\; x^2 + 6x + 9 = -5 + 9 \;\Rightarrow\; (x + 3)^2 = 4" />
                        </div>
                    </div>
                </Reveal>
                <Reveal showAt={4} fragmentIndex={fragmentIndex}>
                    <div className="bg-emerald-50/80 border border-emerald-200 p-4 rounded-2xl">
                        <div className="text-lg text-center mb-3">
                            <BlockMath math="\text{Step 3:}\; x + 3 = \pm 2" />
                        </div>
                        <div className="flex gap-4 justify-center">
                            <span className="px-5 py-2 rounded-xl bg-white border-2 border-emerald-300 text-emerald-700 text-xl font-bold">
                                <InlineMath math="x = -1" />
                            </span>
                            <span className="px-5 py-2 rounded-xl bg-white border-2 border-emerald-300 text-emerald-700 text-xl font-bold">
                                <InlineMath math="x = -5" />
                            </span>
                        </div>
                    </div>
                </Reveal>
            </div>
            <div className="col-span-5">
                <ParabolaChart
                    height={420}
                    range={[-7, 1]}
                    yRange={[-5, 12]}
                    stepY={2}
                    curves={[
                        {
                            id: "main",
                            fn: (x) => x * x + 6 * x + 5,
                            color: PALETTE.primary,
                            hidden: fragmentIndex < 0,
                            label: "y = x² + 6x + 5",
                        },
                    ]}
                    markers={[
                        { x: -1, y: 0, color: PALETTE.red, label: "x = -1", hidden: fragmentIndex < 4 },
                        { x: -5, y: 0, color: PALETTE.red, label: "x = -5", hidden: fragmentIndex < 4 },
                    ]}
                />
            </div>
        </div>
    </Shell>
);

/* ============ 8. quadratic-formula ============ */
const QuadraticFormulaSlide = ({ slide, fragmentIndex }) => (
    <Shell>
        <SlideHeader kicker="Method · The Formula" title={slide.title} />
        <div className="flex-1 grid grid-cols-12 gap-10 items-start max-w-6xl mx-auto w-full">
            <div className="col-span-7 space-y-4">
                <Reveal showAt={0} fragmentIndex={fragmentIndex}>
                    <div
                        className="p-7 text-center text-white rounded-2xl"
                        style={{
                            background: "linear-gradient(135deg, #6366f1, #3b82f6)",
                            boxShadow: "0 24px 60px rgba(99,102,241,0.30)",
                        }}
                    >
                        <p className="text-xs uppercase tracking-[0.28em] text-white/80 font-semibold mb-3">
                            The master key — works for every quadratic
                        </p>
                        <div className="text-3xl">
                            <BlockMath math="x = \dfrac{-b \pm \sqrt{b^2 - 4ac}}{2a}" />
                        </div>
                    </div>
                </Reveal>
                <Reveal showAt={1} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/85 p-5 rounded-2xl border border-white/70 text-center">
                        <p className="text-xs uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2">
                            Worked example
                        </p>
                        <div className="text-2xl mb-2">
                            <BlockMath math="2x^2 + 3x - 2 = 0" />
                        </div>
                        <p className="text-lg text-[#14202a]">
                            <span className="text-blue-500 font-bold">a</span> = 2, <span className="text-emerald-500 font-bold">b</span> = 3, <span className="text-amber-500 font-bold">c</span> = -2
                        </p>
                    </div>
                </Reveal>
                <Reveal showAt={2} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/80 p-4 rounded-2xl border-l-4 border-indigo-500 text-center">
                        <div className="text-lg">
                            <BlockMath math="x = \dfrac{-3 \pm \sqrt{9 + 16}}{4} = \dfrac{-3 \pm 5}{4}" />
                        </div>
                    </div>
                </Reveal>
                <Reveal showAt={3} fragmentIndex={fragmentIndex}>
                    <div className="bg-emerald-50/80 border border-emerald-200 p-4 rounded-2xl">
                        <p className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-semibold mb-3 text-center">
                            Two roots from one formula
                        </p>
                        <div className="flex gap-4 justify-center">
                            <span className="px-5 py-2 rounded-xl bg-white border-2 border-emerald-300 text-emerald-700 text-xl font-bold">
                                <InlineMath math="x = \tfrac{1}{2}" />
                            </span>
                            <span className="px-5 py-2 rounded-xl bg-white border-2 border-emerald-300 text-emerald-700 text-xl font-bold">
                                <InlineMath math="x = -2" />
                            </span>
                        </div>
                    </div>
                </Reveal>
            </div>
            <div className="col-span-5">
                <ParabolaChart
                    height={420}
                    range={[-4, 3]}
                    yRange={[-5, 12]}
                    stepY={2}
                    curves={[
                        {
                            id: "main",
                            fn: (x) => 2 * x * x + 3 * x - 2,
                            color: PALETTE.primary,
                            hidden: fragmentIndex < 1,
                            label: "y = 2x² + 3x - 2",
                        },
                    ]}
                    markers={[
                        { x: 0.5, y: 0, color: PALETTE.red, label: "x = ½", hidden: fragmentIndex < 3 },
                        { x: -2, y: 0, color: PALETTE.red, label: "x = -2", hidden: fragmentIndex < 3 },
                    ]}
                />
            </div>
        </div>
    </Shell>
);

/* ============ 9. discriminant ============ */
const DiscriminantSlide = ({ slide, fragmentIndex }) => (
    <Shell>
        <SlideHeader kicker="Concept · The Discriminant" title={slide.title} />
        <div className="flex-1 grid grid-cols-12 gap-10 items-start max-w-6xl mx-auto w-full">
            <div className="col-span-5 space-y-4">
                <Reveal showAt={0} fragmentIndex={fragmentIndex}>
                    <div
                        className="p-5 rounded-2xl text-center"
                        style={{
                            background: "linear-gradient(135deg, rgba(99,102,241,0.10), rgba(59,130,246,0.10))",
                            border: "1px solid rgba(99,102,241,0.25)",
                        }}
                    >
                        <p className="text-xs uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2">
                            Definition
                        </p>
                        <div className="text-3xl text-[#14202a]">
                            <BlockMath math="D = b^2 - 4ac" />
                        </div>
                        <p className="text-sm text-[#4a5662] mt-1 italic">
                            The bit under the square root
                        </p>
                    </div>
                </Reveal>
                <Reveal showAt={1} fragmentIndex={fragmentIndex}>
                    <div className="p-4 bg-emerald-50/80 border-l-4 border-emerald-500 rounded-r-2xl">
                        <p className="text-lg font-bold text-emerald-800">
                            <InlineMath math="D > 0" />
                        </p>
                        <p className="text-base text-emerald-700">
                            Two <strong>distinct</strong> real roots — graph cuts the x-axis at two points
                        </p>
                    </div>
                </Reveal>
                <Reveal showAt={2} fragmentIndex={fragmentIndex}>
                    <div className="p-4 bg-amber-50/80 border-l-4 border-amber-500 rounded-r-2xl">
                        <p className="text-lg font-bold text-amber-800">
                            <InlineMath math="D = 0" />
                        </p>
                        <p className="text-base text-amber-700">
                            Two <strong>equal</strong> real roots — graph just touches the x-axis
                        </p>
                    </div>
                </Reveal>
                <Reveal showAt={3} fragmentIndex={fragmentIndex}>
                    <div className="p-4 bg-rose-50/80 border-l-4 border-rose-500 rounded-r-2xl">
                        <p className="text-lg font-bold text-rose-800">
                            <InlineMath math="D < 0" />
                        </p>
                        <p className="text-base text-rose-700">
                            <strong>No real roots</strong> — graph never touches the x-axis
                        </p>
                    </div>
                </Reveal>
            </div>
            <div className="col-span-7">
                <ParabolaChart
                    height={460}
                    range={[-4, 6]}
                    yRange={[-4, 12]}
                    stepY={2}
                    curves={[
                        {
                            id: "d>0",
                            fn: (x) => x * x - 5 * x + 6,
                            color: PALETTE.green,
                            hidden: fragmentIndex < 1,
                            label: "y = x² - 5x + 6  (D > 0)",
                        },
                        {
                            id: "d=0",
                            fn: (x) => x * x - 4 * x + 4,
                            color: PALETTE.amber,
                            hidden: fragmentIndex < 2,
                            label: "y = x² - 4x + 4  (D = 0)",
                        },
                        {
                            id: "d<0",
                            fn: (x) => x * x + 2 * x + 5,
                            color: PALETTE.red,
                            hidden: fragmentIndex < 3,
                            label: "y = x² + 2x + 5  (D < 0)",
                        },
                    ]}
                    markers={[
                        { x: 2, y: 0, color: PALETTE.green, hidden: fragmentIndex < 1 },
                        { x: 3, y: 0, color: PALETTE.green, hidden: fragmentIndex < 1 },
                        { x: 2, y: 0, color: PALETTE.amber, hidden: fragmentIndex < 2 },
                    ]}
                />
            </div>
        </div>
    </Shell>
);

/* ============ 10. max-min-vertex ============ */
const MaxMinVertexSlide = ({ slide, fragmentIndex }) => (
    <Shell>
        <SlideHeader kicker="Concept · Vertex" title={slide.title} />
        <div className="flex-1 grid grid-cols-12 gap-10 items-start max-w-6xl mx-auto w-full">
            <div className="col-span-5 space-y-4">
                <Reveal showAt={0} fragmentIndex={fragmentIndex}>
                    <div className="bg-white/80 p-5 rounded-2xl border border-white/70">
                        <p className="text-xl text-[#14202a] leading-relaxed">
                            The <strong>vertex</strong> is the turning point of a parabola — the highest or lowest point of the curve.
                        </p>
                    </div>
                </Reveal>
                <Reveal showAt={1} fragmentIndex={fragmentIndex}>
                    <div className="p-4 bg-emerald-50/80 border-l-4 border-emerald-500 rounded-r-2xl">
                        <p className="text-lg font-bold text-emerald-800">
                            Upward parabola (<InlineMath math="a > 0" />)
                        </p>
                        <p className="text-base text-emerald-700">
                            Vertex is the <strong>minimum</strong> — lowest point of the U.
                        </p>
                    </div>
                </Reveal>
                <Reveal showAt={2} fragmentIndex={fragmentIndex}>
                    <div className="p-4 bg-rose-50/80 border-l-4 border-rose-500 rounded-r-2xl">
                        <p className="text-lg font-bold text-rose-800">
                            Downward parabola (<InlineMath math="a < 0" />)
                        </p>
                        <p className="text-base text-rose-700">
                            Vertex is the <strong>maximum</strong> — highest point of the flipped U.
                        </p>
                    </div>
                </Reveal>
            </div>
            <div className="col-span-7">
                <ParabolaChart
                    height={460}
                    range={[-4, 4]}
                    yRange={[-6, 6]}
                    stepY={2}
                    curves={[
                        {
                            id: "up",
                            fn: (x) => x * x - 3,
                            color: PALETTE.green,
                            hidden: fragmentIndex < 1,
                            label: "y = x² - 3",
                        },
                        {
                            id: "down",
                            fn: (x) => -(x * x) + 3,
                            color: PALETTE.red,
                            hidden: fragmentIndex < 2,
                            label: "y = -x² + 3",
                        },
                    ]}
                    markers={[
                        { x: 0, y: -3, color: PALETTE.green, label: "Minimum", hidden: fragmentIndex < 1 },
                        { x: 0, y: 3, color: PALETTE.red, label: "Maximum", hidden: fragmentIndex < 2 },
                    ]}
                />
            </div>
        </div>
    </Shell>
);

/* ===================== dispatcher ===================== */
export const isQuadraticRichSlide = (slideId) =>
    [
        "what-is-quadratic",
        "shape-of-x2",
        "why-curves-open",
        "roots-intercepts",
        "factor-form-bridge",
        "factorisation-method",
        "completing-square",
        "quadratic-formula",
        "discriminant",
        "max-min-vertex",
    ].includes(slideId);

const QuadraticSlide = ({ slide, fragmentIndex }) => {
    switch (slide.id) {
        case "what-is-quadratic":
            return <WhatIsQuadraticSlide slide={slide} fragmentIndex={fragmentIndex} />;
        case "shape-of-x2":
            return <ShapeOfX2Slide slide={slide} fragmentIndex={fragmentIndex} />;
        case "why-curves-open":
            return <WhyCurvesOpenSlide slide={slide} fragmentIndex={fragmentIndex} />;
        case "roots-intercepts":
            return <RootsInterceptsSlide slide={slide} fragmentIndex={fragmentIndex} />;
        case "factor-form-bridge":
            return <FactorFormBridgeSlide slide={slide} fragmentIndex={fragmentIndex} />;
        case "factorisation-method":
            return <FactorisationMethodSlide slide={slide} fragmentIndex={fragmentIndex} />;
        case "completing-square":
            return <CompletingSquareSlide slide={slide} fragmentIndex={fragmentIndex} />;
        case "quadratic-formula":
            return <QuadraticFormulaSlide slide={slide} fragmentIndex={fragmentIndex} />;
        case "discriminant":
            return <DiscriminantSlide slide={slide} fragmentIndex={fragmentIndex} />;
        case "max-min-vertex":
            return <MaxMinVertexSlide slide={slide} fragmentIndex={fragmentIndex} />;
        default:
            return null;
    }
};

export default QuadraticSlide;
